import { useRef, useState } from 'react';
import Papa from 'papaparse';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import SportBadge from '@/components/ui/SportBadge';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { importGuidance, ImportReport, ImportRow } from '@/api/guidanceService';
import { toaster } from '@/components/ui/toaster';

interface GuidanceImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

const stripAccents = (value: string) =>
  value.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

const pick = (row: Record<string, string>, matcher: (key: string) => boolean): string => {
  const key = Object.keys(row).find((k) => matcher(stripAccents(k)));
  return key ? (row[key] ?? '').trim() : '';
};

const isWarmupKey = (k: string) => k.includes('echauffement') || k.includes('warmup');
const isCooldownKey = (k: string) =>
  k.includes('recuperation') || k.includes('retour au calme') || k.includes('cooldown');

const mapRows = (parsed: Record<string, string>[], headers: string[]): ImportRow[] => {
  const normalizedHeaders = headers.map(stripAccents);
  const hasWarmup = normalizedHeaders.some(isWarmupKey);
  const hasCooldown = normalizedHeaders.some(isCooldownKey);
  return parsed.map((row) => ({
    theme: pick(row, (k) => k.includes('theme')),
    sport: pick(row, (k) => k === 'sport' || k.includes('sport')),
    coachNote: pick(row, (k) => k.includes('coach')),
    nutrition: pick(row, (k) => k.includes('nutrition')),
    warmupPattern: hasWarmup ? pick(row, isWarmupKey) : undefined,
    cooldownPattern: hasCooldown ? pick(row, isCooldownKey) : undefined,
  }));
};

const truncate = (value?: string | null) =>
  value ? (value.length > 40 ? `${value.slice(0, 40)}…` : value) : '—';

const GuidanceImportModal = ({ open, onClose, onImported }: GuidanceImportModalProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setFileName('');
    setRows([]);
    setReport(null);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (file: File) => {
    setReport(null);
    setFileName(file.name);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (result) => {
        const mapped = mapRows(result.data, result.meta.fields ?? []).filter((r) => r.theme || r.sport);
        setRows(mapped);
        if (mapped.length === 0) {
          toaster.create({ title: 'Aucune ligne exploitable dans le fichier', type: 'error' });
          return;
        }
        setBusy(true);
        try {
          setReport(await importGuidance(mapped, true));
        } catch {
          toaster.create({ title: 'Analyse du fichier impossible', type: 'error' });
        } finally {
          setBusy(false);
        }
      },
      error: () => toaster.create({ title: 'Lecture du fichier impossible', type: 'error' }),
    });
  };

  const handleCommit = async () => {
    setBusy(true);
    try {
      const result = await importGuidance(rows, false);
      toaster.create({ title: `${result.summary.imported} contenu(s) importé(s)`, type: 'success' });
      onImported();
      handleClose();
    } catch {
      toaster.create({ title: 'Import impossible', type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="xl"
      title="Importer un CSV"
      footer={
        <>
          <Button variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button onClick={handleCommit} disabled={!report || report.summary.resolved === 0 || busy}>
            {busy ? 'Import…' : `Importer ${report?.summary.resolved ?? 0} contenu(s)`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div
          onClick={() => inputRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-gray-300 py-8 text-center text-sm text-gray-500 hover:border-brand-500 dark:border-gray-700"
        >
          <p className="font-medium text-gray-700 dark:text-gray-200">
            {fileName || 'Déposer le fichier .csv'}
          </p>
          <p>Colonnes attendues : thème, sport, mot du coach, nutrition, échauffement, récupération (facultatives)</p>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          />
        </div>

        {report && (
          <>
            <div className="flex flex-wrap gap-3 text-sm">
              <Badge color="green">{report.summary.resolved} valides</Badge>
              <Badge color="red">{report.summary.unresolved} en erreur</Badge>
              <Badge color="gray">{report.summary.total} lignes</Badge>
            </div>
            <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-800">
              <Table>
                <THead>
                  <tr>
                    <TH>Statut</TH>
                    <TH>Thème</TH>
                    <TH>Sport</TH>
                    <TH>Échauffement</TH>
                    <TH>Récupération</TH>
                    <TH>Détail</TH>
                  </tr>
                </THead>
                <TBody>
                  {report.rows.map((r) => (
                    <TR key={r.index}>
                      <TD>
                        {r.status === 'ok' ? <Badge color="green">ok</Badge> : <Badge color="red">erreur</Badge>}
                      </TD>
                      <TD>{r.themeLabel ?? r.theme}</TD>
                      <TD>
                        {r.sportIdentifier ?? r.sport ? (
                          <SportBadge identifier={r.sportIdentifier ?? r.sport} />
                        ) : (
                          '—'
                        )}
                      </TD>
                      <TD className="text-gray-500" title={rows[r.index]?.warmupPattern ?? ''}>
                        {truncate(rows[r.index]?.warmupPattern)}
                      </TD>
                      <TD className="text-gray-500" title={rows[r.index]?.cooldownPattern ?? ''}>
                        {truncate(rows[r.index]?.cooldownPattern)}
                      </TD>
                      <TD className="text-gray-500">{r.reason ?? '—'}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default GuidanceImportModal;
